import React, { useEffect, useMemo, useState } from 'react';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import AlertsComponent from 'components/Alert/AlertsComponent';
import { Typography } from '@tvg/fuse/typography';
import { Radio } from '@tvg/fuse/radio';
import { RadioGroup } from '@tvg/fuse/radio-group';
import { GridRow, GridItem } from '@tvg/fuse/grid';
import { Link } from '@tvg/fuse/link';
import BrandTypes from 'constants/brand';
import OptionButton from './OptionButton/OptionButton';
import ShowMoreOption from './ShowMoreOption';
import { getArticle } from '../../utilities/util';
import { OptionSelectorProps } from './OptionSelectorProps';
import styles from './OptionSelector.module.css';

const SIZE_GUIDE_BRAND: Record<string, string> = { nike: '/size-guide/nike.page' };

function OptionSelector({ skus, formattedName, name, site, brand }: OptionSelectorProps) {
  const isColourOption = name === 'colour';

  const sizeGuideUrl = SIZE_GUIDE_BRAND[brand?.toLowerCase() || ''];
  const hasSizeGuide = Boolean(sizeGuideUrl) && site === BrandTypes.VERY && name === 'size';

  const {
    isOptionSelectorError,
    setOptionSelectorError,
    selectedSku,
    uniqueSKUList,
    selectedOptions,
    handleOptionSelect,
  } = useSelectedSkuContext();

  const [isExpanded, setIsExpanded] = useState(false);

  const [errorMessage, setErrorMessage] = useState({
    message: `Please select ${getArticle(formattedName)}`,
    isShown: false,
  });

  const optionLengths = useMemo(() => {
    const lengths = {
      extraLarge: false,
      large: false,
      medium: false,
    };

    uniqueSKUList[name]?.some((option) => {
      const { length } = option.value;

      if (length >= 20) {
        lengths.extraLarge = true;
        return true;
      }

      if (length >= 12) {
        lengths.large = true;
      }

      if (length >= 6) {
        lengths.medium = true;
      }

      return false;
    });

    return lengths;
  }, [uniqueSKUList, name]);

  const numberOfColumns = useMemo(() => {
    if (isColourOption || optionLengths.large) {
      return 6;
    }

    if (optionLengths.medium) {
      return 4;
    }

    return 3;
  }, [isColourOption, optionLengths]);

  const isRadioOption = !isColourOption && optionLengths.extraLarge;

  /**
   * Finds the first non-empty swatch for a given option value.
   *
   * This function iterates over the list of SKUs and returns the swatches of the first SKU
   * that matches the provided option value and has non-empty swatches.
   *
   * @param {string} value - The value of the option to match against the SKUs.
   * @returns {object | undefined} - The swatches of the first matching SKU, or undefined if no matching SKU is found.
   *
   */
  const findSwatches = (value: string) => {
    const matchingSku = skus.find(
      (sku) =>
        sku.options[name as keyof typeof sku.options] === value &&
        sku.swatches &&
        Object.keys(sku.swatches).length > 0 &&
        (sku.swatches.colour || sku.swatches.image),
    );

    return matchingSku ? matchingSku.swatches : undefined;
  };

  const handleSelectedSku = (sku: { value: string; disabled: boolean }) => {
    setErrorMessage({
      ...errorMessage,
      isShown: false,
    });

    handleOptionSelect(sku.value, name);
  };

  const showError = () => isOptionSelectorError.value && errorMessage.isShown;

  const renderError = () => (
    <GridRow>
      <GridItem data-testid={`option-selector-error-${name}`} xxs={12}>
        <AlertsComponent errorMessage={errorMessage.message} />
      </GridItem>
    </GridRow>
  );

  useEffect(() => {
    const isOptionSelected = Object.keys(selectedOptions).some((key) => key === name);

    setErrorMessage({
      ...errorMessage,
      isShown: !isOptionSelected,
    });
  }, [isOptionSelectorError]);

  useEffect(() => {
    if (selectedSku) setOptionSelectorError({ value: false });
  }, [selectedSku]);

  const underscoresSlashesSpacesPattern = /[_/\s]/g;

  const renderOptionButtons = () => (
    <GridRow>
      {uniqueSKUList &&
        uniqueSKUList[name]?.map((sku) => (
          <GridItem
            xxs={numberOfColumns}
            key={sku.value}
            className={[
              !isExpanded ? styles.gridItemCollapsed : '',
              numberOfColumns === 3 ? styles.xxs3 : '',
              numberOfColumns === 4 ? styles.xxs4 : '',
              numberOfColumns === 6 ? styles.xxs6 : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <OptionButton
              id={`${name}-${sku.value}`.replace(underscoresSlashesSpacesPattern, '-').toLowerCase()}
              name={name}
              label={sku.value}
              checked={(uniqueSKUList && selectedOptions[name]) === sku.value}
              disabled={sku.disabled}
              swatchColour={isColourOption ? findSwatches(sku.value)?.colour : undefined}
              swatchImage={isColourOption ? findSwatches(sku.value)?.image : undefined}
              handleOnClick={() => handleSelectedSku(sku)}
              key={sku.value}
            />
          </GridItem>
        ))}
    </GridRow>
  );

  const renderRadioOptions = () => (
    <RadioGroup dataTestId="radio-group" groupName="radio-group">
      {uniqueSKUList &&
        uniqueSKUList[name]?.map((sku) => (
          <Radio
            className={styles.optionRadio}
            dataTestId={`radio-button-${sku.value.split(' ').join('-')}`}
            clickCallback={() => handleSelectedSku(sku)}
            radioLabel={sku.value}
            radioName={sku.value}
            disabled={sku.disabled}
            key={sku.value}
            checked={(uniqueSKUList && selectedOptions[name]) === sku.value}
            defaultChecked={(uniqueSKUList && selectedOptions[name]) === sku.value}
          />
        ))}
    </RadioGroup>
  );
  return (
    <div data-testid={`option-selector-${name}`.replace(underscoresSlashesSpacesPattern, '-').toLowerCase()}>
      <div className={!isRadioOption ? styles.containerNotRadio : undefined}>
        <GridRow className={styles.headerRow}>
          <GridItem xxs={hasSizeGuide ? 8 : 12}>
            <Typography
              id={`option-selector-label-${name}`}
              size="1"
              tag="p"
              color={showError() ? 'var(--color-universal-redMid)' : undefined}
            >
              Pick your {formattedName}
            </Typography>
          </GridItem>

          {hasSizeGuide && sizeGuideUrl ? (
            <GridItem xxs={4} className={styles.sizeGuideItem}>
              <Link href={sizeGuideUrl} data-testid="size-guide-link" size="medium" target="_blank" rel="noopener">
                Size guide
              </Link>
            </GridItem>
          ) : null}
        </GridRow>
        {showError() ? renderError() : null}

        {isRadioOption ? renderRadioOptions() : renderOptionButtons()}

        {uniqueSKUList && uniqueSKUList[name]?.length > 12 && (
          <ShowMoreOption isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
        )}
      </div>
    </div>
  );
}

export default OptionSelector;
